import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
    Box,
    Typography,
    Grid,
    Fab,
    Badge,
    Tabs,
    Tab,
    Alert,
    CircularProgress,
} from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import db from "state/db";
import ProductCard from "./ProductCard";
import CartDrawer from "./CartDrawer";
import CheckoutDialog from "./CheckoutDialog";
import useInflightSession from "./hooks/useInflightSession";
import useOnlineStatus from "./hooks/useOnlineStatus";


const SalesFloor = ({ session }) => {
    const user = useSelector((state) => state.auth.user);
    const isOnline = useOnlineStatus();
    const { addOrder } = useInflightSession(user?.crewCode, user?.crewName);

    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [activeCategory, setActiveCategory] = useState("All");
    const [loadingProducts, setLoadingProducts] = useState(true);

    // cart: [{ product, qty, totalAmount, commission }]
    const [cart, setCart] = useState([]);
    const [cartOpen, setCartOpen] = useState(false);
    const [checkoutOpen, setCheckoutOpen] = useState(false);

    useEffect(() => {
        db.products.toArray().then((prods) => {
            const sorted = prods.slice().sort((a, b) => (a.sortOrder ?? 9999) - (b.sortOrder ?? 9999));
            setProducts(sorted);
            const cats = [...new Set(sorted.map((p) => p.category).filter(Boolean))];
            setCategories(cats);
            setLoadingProducts(false);
        });
    }, []);

    const addToCart = useCallback((product) => {
        setCart((prev) => {
            const existing = prev.find((i) => i.product._id === product._id);
            if (existing) {
                return prev.map((i) =>
                    i.product._id === product._id
                        ? {
                              ...i,
                              qty: i.qty + 1,
                              totalAmount: (i.qty + 1) * (product.price ?? 0),
                              commission: (i.qty + 1) * (product.price ?? 0) * (product.directCommissionRate ?? 0),
                          }
                        : i
                );
            }
            return [
                ...prev,
                {
                    product,
                    qty: 1,
                    totalAmount: product.price ?? 0,
                    commission: (product.price ?? 0) * (product.directCommissionRate ?? 0),
                },
            ];
        });
    }, []);

    const handleQtyChange = useCallback((idx, qty) => {
        if (qty < 1) return;
        setCart((prev) =>
            prev.map((item, i) =>
                i === idx
                    ? {
                          ...item,
                          qty,
                          totalAmount: qty * (item.product.price ?? 0),
                          commission: qty * (item.product.price ?? 0) * (item.product.directCommissionRate ?? 0),
                      }
                    : item
            )
        );
    }, []);

    const handleRemove = useCallback((idx) => {
        setCart((prev) => prev.filter((_, i) => i !== idx));
    }, []);

    const decrementFromCard = useCallback((product) => {
        setCart((prev) => {
            const item = prev.find((i) => i.product._id === product._id);
            if (!item) return prev;
            if (item.qty === 1) return prev.filter((i) => i.product._id !== product._id);
            return prev.map((i) =>
                i.product._id === product._id
                    ? { ...i, qty: i.qty - 1, totalAmount: (i.qty - 1) * (product.price ?? 0), commission: (i.qty - 1) * (product.price ?? 0) * (product.directCommissionRate ?? 0) }
                    : i
            );
        });
    }, []);

    const handleCheckout = useCallback(
        async (paymentMethod, note) => {
            try {
                const items = cart.map((i) => ({
                    productId: i.product._id,
                    productCode: i.product.productCode,
                    productName: i.product.productName,
                    unit: i.product.unit,
                    category: i.product.category,
                    unitPrice: i.product.price ?? 0,
                    quantity: i.qty,
                    totalAmount: i.totalAmount,
                    commissionRate: i.product.directCommissionRate ?? 0,
                    commission: i.commission,
                }));

                await addOrder(session._id, items, paymentMethod, note);
                setCart([]);
                setCheckoutOpen(false);
                toast.success("Order recorded.");
            } catch {
                toast.error("Failed to save order.");
            }
        },
        [cart, session._id, addOrder]
    );

    const cartTotal = cart.reduce((s, i) => s + i.qty, 0);

    const filtered =
        activeCategory === "All" ? products : products.filter((p) => p.category === activeCategory);

    if (loadingProducts) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (products.length === 0) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert 
                    severity="warning"
                    sx={{ alignItems: "center"}}
                >
                    No products cached. Go to the Session tab and click "Download Catalog" while online.
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ position: "relative" }}>
            {/* Category filter */}
            <Tabs
                variant="fullWidth"
                scrollButtons="auto"
                allowScrollButtonsMobile
                value={activeCategory}
                onChange={(_, v) => setActiveCategory(v)}
                slotProps={{ indicator: { style: { height: 2 } } }}
                sx={{ borderBottom: 1, borderColor: "divider", minHeight: 38 }}
            >
                {["All", ...categories].map((cat) => (
                    <Tab
                        key={cat}
                        value={cat}
                        label={cat}
                        sx={{ 
                            textTransform: "none", 
                            minHeight: 38, 
                            py: 1, 
                        }}
                    />
                ))}
            </Tabs>

            {/* Product grid */}
            <Box sx={{ py: 2 }}>
                {filtered.length === 0 ? (
                    <Typography color="text.secondary" textAlign="center" mt={2}>
                        No products in this category.
                    </Typography>
                ) : (
                    <Grid container spacing={1.5}>
                        {filtered?.map((product) => (
                            <Grid 
                                key={product._id}
                                size={{ xs: 12, md: 4, lg: 3 }} 
                            >
                                <ProductCard
                                    product={product}
                                    onAdd={addToCart}
                                    onDecrement={decrementFromCard}
                                    cartQty={cart.find((i) => i.product._id === product._id)?.qty || 0}
                                />
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>

            {/* Cart FAB */}
            <Fab
                color="primary"
                sx={{ position: "fixed", bottom: 60, right: 5 }}
                onClick={() => setCartOpen(true)}
                disabled={cartTotal === 0}
            >
                <Badge badgeContent={cartTotal} color="error">
                    <ShoppingCartIcon />
                </Badge>
            </Fab>

            <CartDrawer
                open={cartOpen}
                cart={cart}
                onClose={() => setCartOpen(false)}
                onQtyChange={handleQtyChange}
                onRemove={handleRemove}
                onCheckout={() => setCheckoutOpen(true)}
            />

            <CheckoutDialog
                open={checkoutOpen}
                cart={cart}
                onClose={() => setCheckoutOpen(false)}
                onConfirm={handleCheckout}
                isOnline={isOnline}
            />
        </Box>
    );
};

export default SalesFloor;
